import { PartyStatusDefinition, StatusTransition } from 'services/api/WorkflowServiceAPI';

export type StatusModel = {
    partyStatuses: PartyStatusDefinition[];
    statusTransitions: StatusTransition[];
};

export const EMPTY_STATUS_MODEL: StatusModel = { partyStatuses: [], statusTransitions: [] };

// The form fills in '' and false where the API omits them, so an untouched
// prefilled form would fail a plain deep-equal.
const canonicalStatuses = (statuses: PartyStatusDefinition[] = []) =>
    statuses.map((status) => ({
        name: status.name?.trim() ?? '',
        terminal: !!status.terminal,
        crmIntegrationName: status.crmIntegrationName?.trim() ?? '',
        pushToCrm: !!status.pushToCrm,
    }));

const canonicalTransitions = (transitions: StatusTransition[] = []) =>
    transitions.map((transition) => ({
        logic: transition.logic ?? 'and',
        target: transition.target?.trim() ?? '',
        rules: (transition.rules ?? []).map((rule) => ({
            variable: rule.variable,
            condition: rule.condition,
            value: rule.value,
        })),
    }));

export const isStatusModelChanged = (current: StatusModel, baseline: StatusModel): boolean =>
    JSON.stringify(canonicalStatuses(current.partyStatuses)) !==
        JSON.stringify(canonicalStatuses(baseline.partyStatuses)) ||
    JSON.stringify(canonicalTransitions(current.statusTransitions)) !==
        JSON.stringify(canonicalTransitions(baseline.statusTransitions));

export const isTransitionsChanged = (current: StatusTransition[], baseline: StatusTransition[]): boolean =>
    JSON.stringify(canonicalTransitions(current)) !== JSON.stringify(canonicalTransitions(baseline));

/**
 * What a create request should carry for the status model.
 *
 * Baseline is the fetched defaults for a new workflow, empty for a duplicate so
 * the source model always survives the copy. Sending an untouched baseline back
 * is rejected outright for tenants whose statuses come from dedicated code, and
 * fails the whole workflow.
 *
 * Both lists go together: create discards the seeded transitions as soon as a
 * request carries partyStatuses.
 */
export const statusModelPayload = (current: StatusModel, baseline: StatusModel): Partial<StatusModel> => {
    if (!isStatusModelChanged(current, baseline)) return {};
    return {
        partyStatuses: current.partyStatuses,
        statusTransitions: current.statusTransitions,
    };
};
